import { useCallback, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'
import type { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import type { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import type { TradingSpreadKind } from '@ebalance/proto/bfftypes/v1/trading_spread_kind_pb'
import type { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import {
  SUPPLY_BILATERAL_FILTERS_STORAGE_KEY,
  type SupplyBilateralFilters,
  defaultSupplyBilateralFilters,
  supplyBilateralFiltersCodec,
} from '~/pages/SupplyBilateral/models/persistedFilters'

export type FilterState = {
  buySellFilter: Side | undefined
  executionRouteFilter: TradingExecutionRoute | undefined
  timeZoneTypeFilter: TradingTimeZoneType | undefined
  /** 受渡区分フィルタ (enum 固定値)。 旧 master id (UUID) から enum に変更 (delivery-type-enum-spec §6)。 */
  deliveryTypeFilter: TradingDeliveryType | undefined
  areaCodeFilter: AreaCode | undefined
  partnerNameFilter: string | undefined
  deliveryPeriodFrom: ProtoDate | undefined
  deliveryPeriodTo: ProtoDate | undefined
  /** ステータスフィルタ (= 成立済 / ドラフト / 期間満了 / 取消)。 undefined = すべて (既定)。 */
  statusFilter: TradingContractStatus | undefined
  /** スプレッド種別フィルタ (= 単独 / 異エリア / 期間 / 時間帯 / その他)。 undefined = すべて。 */
  spreadKindFilter: TradingSpreadKind | undefined
  /** 期間満了表示。 false (既定) で status=EXPIRED 除外、 true で含める。 */
  includeExpired: boolean
  page: number
  pageSize: number
}

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50

/**
 * フィルタ由来の値に既定のページャ位置を載せて `FilterState` にする。
 *
 * 初期化 (= 保存値の復元) と「クリア」(= `defaultSupplyBilateralFilters`) の
 * 両方がこの 1 箇所を通る (= 同じ合成を 2 箇所に手書きすると、片方だけ直す事故に
 * なる)。フィルタ値の既定は `models/persistedFilters.ts` が SoT (= 保持・復元と
 * 同じ既定を使う)、ページャは保持対象外なので本 hook が持つ。
 */
function withDefaultPager(filters: SupplyBilateralFilters): FilterState {
  return {
    ...filters,
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  }
}

/**
 * フィルタの選択状態を localStorage へ保存する。
 *
 * `FilterState` をそのまま渡す (= `SupplyBilateralFilters` は
 * `Omit<FilterState, 'page' | 'pageSize'>` なので代入可能)。保持対象の切り出しは
 * `supplyBilateralFiltersCodec` が宣言した項目だけを書く
 * ことで行われ、`page` / `pageSize` は保存に載らない。直列化・復元・既定値・検証は
 * `models/persistedFilters.ts` の純関数が SoT (= フィルタ項目を足す時に手を入れる
 * のはあちらだけ)。
 */
function persistFilters(next: FilterState, keepStoredPeriod: boolean): void {
  // ワークフロー起点で開いている間の受渡期間は対象日由来 (AC-SBL-022 の ※) なので
  // 保存しない。保存すると、ワークフロー外で開き直した時に対象日の 1 日へ絞り込まれた
  // まま復元される。受渡期間だけは保存済みの値を書き戻し、他のフィルタは通常どおり保存する。
  const toStore = keepStoredPeriod
    ? (() => {
        const stored = supplyBilateralFiltersCodec.deserialize(
          getLocalStorageItem(SUPPLY_BILATERAL_FILTERS_STORAGE_KEY),
        )
        return {
          ...next,
          deliveryPeriodFrom: stored.deliveryPeriodFrom,
          deliveryPeriodTo: stored.deliveryPeriodTo,
        }
      })()
    : next
  setLocalStorageItem(
    SUPPLY_BILATERAL_FILTERS_STORAGE_KEY,
    supplyBilateralFiltersCodec.serialize(toStore),
  )
}

/**
 * ワークフローの対象日を受渡期間の「対象日〜対象日」に載せる
 * (AC-SBL-015 / 022 / 025 の ※)。対象日が無ければ受け取った値をそのまま返す。
 */
function withWorkflowPeriod(
  filters: SupplyBilateralFilters,
  workflowTargetDate: ProtoDate | undefined,
): SupplyBilateralFilters {
  if (workflowTargetDate === undefined) return filters
  return {
    ...filters,
    deliveryPeriodFrom: workflowTargetDate,
    deliveryPeriodTo: workflowTargetDate,
  }
}

function protoDateKey(d: ProtoDate | undefined): string | undefined {
  return d === undefined ? undefined : `${d.year}-${d.month}-${d.day}`
}

export type UseFilterStateOptions = {
  /**
   * `?workflowId=` 付きで開いた時のワークフロー対象日 (取得完了後のみ)。
   * 指定されると受渡期間の初期値と「クリア」後の値を対象日〜対象日にする。
   * 期間は固定せず、利用者は変更できる (AC-WFW-006a の例外。AC-SBL-022 の ※)。
   */
  workflowTargetDate?: ProtoDate
  /**
   * `?workflowId=` 付きで開いているか。true の間は受渡期間を localStorage に
   * 保存しない (対象日の取得前に操作された場合も含めるため、対象日とは別に受け取る)。
   */
  isWorkflowLinked?: boolean
}

export type UseFilterStateReturn = {
  filter: FilterState
  setFilter: (partial: Partial<FilterState>) => void
  resetFilter: () => void
}

/**
 * 取引契約一覧のフィルタ + ページネーション状態管理 hook。
 *
 * 保持するフィルタ項目は `models/persistedFilters.ts` の
 * `SupplyBilateralFilters` が SoT (spec AC-SBL-021 が挙げるフィルタ一覧とは
 * 一対一ではない — 受渡期間を from / to の 2 項目で持ち、「自社BG・提出者」
 * 「商品形態」は FilterBar 未実装)。
 *
 * 保存が無ければ全フィルタを初期値 (= すべて選択) で始める (AC-SBL-015 [P0])。
 * 保存があれば localStorage から復元する — 契約詳細・48点データへの遷移や再訪を
 * またいで選択状態を保つのは AC-SC-031 [P0] の横断ルールで、 AC-SBL-015 が定める
 * のは**その初期値が何か**。保持対象と除外の理由は `models/persistedFilters.ts` の
 * docstring 参照。ページャ位置 (`page` / `pageSize`) は保持せず、mount のたびに
 * 先頭ページから始める。
 *
 * `?workflowId=` 付きで開いた時は、受渡期間の初期値と「クリア」後の値を
 * ワークフローの対象日〜対象日にする (AC-SBL-015 / 022 / 025 の ※)。
 */
export function useFilterState({
  workflowTargetDate,
  isWorkflowLinked = false,
}: UseFilterStateOptions = {}): UseFilterStateReturn {
  const [filter, setFilterState] = useState<FilterState>(() =>
    withDefaultPager(
      withWorkflowPeriod(
        supplyBilateralFiltersCodec.deserialize(
          getLocalStorageItem(SUPPLY_BILATERAL_FILTERS_STORAGE_KEY),
        ),
        workflowTargetDate,
      ),
    ),
  )

  // 対象日はワークフローの取得後に届くため、届いた (変わった) 時点で受渡期間へ
  // 1 回だけ載せる (外れた時は保存済みの期間へ戻す)。effect ではなく render 中に state を合わせる
  // (React の「props の変化に合わせて state を調整する」形)。effect だと対象日が
  // 届いた render で古い受渡期間のまま一覧の取得が走る。対象日が変わらない限り
  // 再適用しないので、利用者が変えた期間は上書きしない。保存もしない (初期値の
  // 適用は利用者の操作ではない)。
  const targetKey = protoDateKey(workflowTargetDate)
  const [appliedTargetKey, setAppliedTargetKey] = useState(targetKey)
  if (targetKey !== appliedTargetKey) {
    setAppliedTargetKey(targetKey)
    if (workflowTargetDate !== undefined) {
      setFilterState((prev) => ({
        ...prev,
        ...withWorkflowPeriod(prev, workflowTargetDate),
        page: DEFAULT_PAGE,
      }))
    } else if (appliedTargetKey !== undefined) {
      // 画面を開いたまま workflowId が外れた (サイドバーから開き直す等。route は
      // 再 mount しない)。対象日由来の期間を残すと、ワークフロー外の操作で
      // 1 日の期間が保存されるので、ワークフロー外で開いた時と同じ保存済みの
      // 受渡期間へ戻す。
      const stored = supplyBilateralFiltersCodec.deserialize(
        getLocalStorageItem(SUPPLY_BILATERAL_FILTERS_STORAGE_KEY),
      )
      setFilterState((prev) => ({
        ...prev,
        deliveryPeriodFrom: stored.deliveryPeriodFrom,
        deliveryPeriodTo: stored.deliveryPeriodTo,
        page: DEFAULT_PAGE,
      }))
    }
  }

  // **書く契機は利用者の操作だけ** (= setFilter / resetFilter が呼ばれた時)。
  // 保存を effect ではなくハンドラの中に置くことでこれを構造的に保証する。
  // 未操作の初期 mount で既定値を書くと、`~/hooks/usePersistedFilters` の godoc が
  // 挙げるのと同じ事故 (ServerClockGate の待ち上限経路で端末時計由来の初期値が
  // 焼き付く) を、この画面でも自前実装として再現することになる。
  const setFilter = useCallback(
    (partial: Partial<FilterState>): void => {
      // フィルタ条件変更時は page を 1 にリセット。 ただし page 自体を指定した場合は尊重する。
      // partial.page は Partial<FilterState> の page フィールドで number | undefined。
      // page が undefined のときは 1 (先頭ページ) にフォールバックする。
      const nextPage = partial.page ?? 1
      setFilterState((prev) => {
        const next = { ...prev, ...partial, page: nextPage }
        // ページ送りだけの操作では書かない。 page は保持対象外なので保存内容は
        // 1 バイトも変わらず、 書いても無駄な I/O にしかならない。
        const isPageOnly =
          Object.keys(partial).length === 1 && partial.page !== undefined
        if (!isPageOnly) persistFilters(next, isWorkflowLinked)
        return next
      })
    },
    [isWorkflowLinked],
  )

  const resetFilter = useCallback((): void => {
    // 「クリア」も利用者の操作。保存しないと再訪でクリア前の状態が戻る。
    // ワークフロー起点では受渡期間を対象日〜対象日へ戻す (AC-SBL-025 の ※)。
    const defaults = withDefaultPager(
      withWorkflowPeriod(defaultSupplyBilateralFilters(), workflowTargetDate),
    )
    persistFilters(defaults, isWorkflowLinked)
    setFilterState(defaults)
  }, [workflowTargetDate, isWorkflowLinked])

  return { filter, setFilter, resetFilter }
}
